import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Heading from '@react/react-spectrum/Heading'
import Link from '@react/react-spectrum/Link'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import { error as errorToast, info } from '@react/react-spectrum/Toast'

import ChevronLeft from '@react/react-spectrum/Icon/ChevronLeft'
import Edit from '@react/react-spectrum/Icon/Edit'

import COMPONENT_CONFIGS from 'config/components'
import { PATHS } from 'config/constants'
import { useFeatures, useProject, useScreens } from 'hooks'

import { ComponentManager } from 'components/ComponentManager'
import RenameScreenDialog from 'components/RenameScreen'
import SaveIndicator from 'components/SaveIndicator'
import { ScreenBuilderProjectContextProvider } from './context/ScreenBuilderProjectContext'

import { ProjectLoadingState } from './Project'

const ScreenBuilder = () => {
  const history = useHistory()
  const { projectId, screenName } = useParams()
  const features = useFeatures()
  const { project, canEdit, type } = useProject()
  const { screens, setScreenContent, renameScreen } = useScreens()
  const [components, setComponents] = useState([])

  useEffect(() => {
    if (project && !canEdit) {
      info('You are viewing the project in read-only mode. Please contact project owner for edit access.')
    }
  }, [])

  const screen = project.screens[screenName]
  const configs = COMPONENT_CONFIGS[type]

  const screenProduct = project.products?.find((p) => p.id === screen.meta?.product)
  const productLink = screenProduct && PATHS.PROJECT_DETAILS + projectId + PATHS.PRODUCT_DETAILS + screenProduct.id

  const SidebarHeader = productLink ? (
    <div className='ScreenProductHeader'>
      <FieldLabel label='Linked Product'>
        <Link href={productLink}>{screenProduct.name}</Link>
      </FieldLabel>
      <hr />
    </div>
  ) : null

  const onBack = () => {
    if (project) {
      history.push(`${PATHS.PROJECT_DETAILS}${projectId}${PATHS.SCREENS_TAB}`)
    } else {
      history.push(PATHS.HOME)
    }
  }

  const _renameScreen = ({ newTitle, newName }) => {
    renameScreen(screenName, newName, newTitle)
    setTimeout(() => history.push(PATHS.PROJECT_DETAILS + projectId + PATHS.SCREEN_DETAILS + newName), 0)
  }

  useEffect(() => {
    if (Array.isArray(screen.content)) {
      setComponents(screen.content)
    }
  }, [screen])

  return (
    <ScreenBuilderProjectContextProvider project={project}>
      <div className='ScreenBuilder'>
        <div className='ScreenBuilder__header'>
          <span className='ScreenBuilder__title'>
            <Button
              icon={<ChevronLeft size='M' />}
              variant='tool'
              onClick={onBack}
              className='ScreenBuilder__backButton'
              aria-label='Back to project list'
            />
            <Heading size={2}>{project.meta.title + ' / ' + (screen.meta.title || screen.meta.name)}</Heading>
            <SaveIndicator updatedAt={project.updatedAt} />
          </span>
          <span>
            <ModalTrigger>
              <Button icon={<Edit />} disabled={!canEdit}>
                Rename screen
              </Button>
              <RenameScreenDialog screen={screen} screens={screens} onRename={_renameScreen} />
            </ModalTrigger>
          </span>
        </div>
        <div className='ScreenBuilder__contentArea'>
          <ComponentManager
            type={type}
            components={components}
            configs={configs}
            features={features}
            screenName={screenName}
            SidebarHeader={SidebarHeader}
            disabled={!canEdit}
            onUpdate={(cmps) => {
              setComponents(cmps)
              setScreenContent(screenName, cmps)
            }}
          />
        </div>
      </div>
    </ScreenBuilderProjectContextProvider>
  )
}

function ScreenBuilderContainer() {
  const history = useHistory()
  const { projectId, screenName } = useParams()
  const { project, loading, error } = useProject()

  const screen = project?.screens && project?.screens[screenName]

  useEffect(() => {
    if (project?.screens && !screen) {
      errorToast('Screen named ' + screenName + ' does not exist in project ' + projectId + '.')
      history.push(PATHS.HOME)
      return
    }

    if (screen && screen?.meta?.template !== 'FreeformPage' && screen?.meta?.template !== 'FreeformScreen') {
      errorToast(
        `The ${screen?.meta?.title} screen cannot be open in Screen Builder, as it is not based on Freeform Template.`
      )
      history.push(`${PATHS.PROJECT_DETAILS}${project?.meta?.id}${PATHS.SCREENS_TAB}`)
    }
  }, [screen, project])

  useEffect(() => {
    if (error) {
      const { code, message } = error
      errorToast(message)
      if (['NOT_ALLOWED', 'PROJECT_NOT_FOUND'].includes(code)) {
        history.push(PATHS.HOME)
      }
    }
  }, [error, history])

  if (loading) {
    return <ProjectLoadingState />
  }

  return <> {screen && <ScreenBuilder />}</>
}

export default ScreenBuilderContainer
